/**
 * ACRES Promo — dismissible top banner
 *
 * Renders only when the server confirms the user is in the active banner segment
 * AND the configured dismissal cooldown has elapsed. Banner copy is server-driven
 * (headline / cta_label / cta_target) so admins can update it from
 * AcresBannerAdmin without a code deploy.
 */
import React, { useEffect, useState } from 'react';
import { acres as acresUserService } from '../lib/services/acresService';

interface BannerPayload {
  id: string;
  banner_type: 'top_banner' | 'modal_popup' | 'both';
  headline: string;
  body_markdown?: string | null;
  cta_label?: string | null;
  cta_target?: string | null;
}

interface Props {
  onNavigateToAcres: () => void;
  onPayloadResolved?: (payload: BannerPayload | null) => void;
}

export const AcresPromoBanner: React.FC<Props> = ({ onNavigateToAcres, onPayloadResolved }) => {
  const [banner, setBanner] = useState<BannerPayload | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await acresUserService.getActiveBanner();
        if (cancelled) return;
        const payload: BannerPayload | null = res?.data?.banner ?? null;
        const localKey = payload ? `acres_promo_banner_dismissed_${payload.id}` : null;
        if (payload && localKey && localStorage.getItem(localKey) === '1') {
          setBanner(null);
          if (onPayloadResolved) onPayloadResolved(null);
          return;
        }
        setBanner(payload);
        if (onPayloadResolved) onPayloadResolved(payload);
      } catch (err) {
        console.warn('[AcresPromoBanner] fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [onPayloadResolved]);

  const handleDismiss = async () => {
    if (!banner) return;
    setHidden(true);
    try {
      localStorage.setItem(`acres_promo_banner_dismissed_${banner.id}`, '1');
      await acresUserService.dismissBanner(banner.id);
    } catch (err) {
      console.warn('[AcresPromoBanner] dismiss failed:', err);
    }
  };

  const handleCta = () => {
    if (!banner) return;
    if (banner.cta_target && banner.cta_target !== 'acres') {
      // External target — navigate via history; otherwise fall through
      try { window.location.assign(banner.cta_target); return; } catch { /* noop */ }
    }
    onNavigateToAcres();
  };

  if (!banner || hidden) return null;
  if (banner.banner_type === 'modal_popup') return null;

  return (
    <div
      className="bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 text-white shadow-md"
      role="region"
      aria-label="ACRES rewards promotion"
    >
      <div className="container mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">🎁</span>
            <div className="min-w-0">
              <p className="font-semibold text-sm md:text-base truncate">{banner.headline}</p>
              {banner.body_markdown && (
                <p className="text-xs md:text-sm opacity-90 truncate">{banner.body_markdown}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCta}
              className="bg-white/20 hover:bg-white/30 text-white text-xs md:text-sm font-semibold px-3 py-1.5 rounded transition-colors border border-white/40"
            >
              {banner.cta_label || 'Earn Rewards'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-white/90 hover:text-white p-1"
              aria-label="Dismiss ACRES promo banner"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcresPromoBanner;
